/**
 * Button.jsx
 * FILE VERSION: BUTTON-v1.7.40.84
 * CHARLIE VERSION: v1.7.40
 *
 * Real, shared Button component. Shane's locked decision 2026-08-07:
 * approved as the real system-wide standard.
 *
 * Real problem this closes: CHARLIE had NO shared button at all. A real
 * count at build time found 635 inline-styled <button> elements across
 * 100 files. That is exactly why they drift and read as "frumpy" -- not
 * an artistic problem, a structural one. Same consolidation
 * LauncherTile.jsx already did for landing-page tiles, and FilterBar.jsx
 * did for filter chips in the commit before this one.
 *
 * Real, deliberate scope note: this file introduces the standard. It
 * does NOT migrate 635 call sites -- that is a genuine multi-session
 * sweep and is tracked as its own separate item. Pages adopt this
 * incrementally. Nothing breaks in the meantime because every existing
 * inline button keeps working untouched.
 *
 * Real design decisions:
 *   - CSS-drawn, never raster. Scales to any label length. This is
 *     precisely why Marty's Active User Control artwork was NOT used
 *     system-wide -- it is a fixed-aspect PNG with a built-in icon slot
 *     and chevron, correct for the Switch User pill and wrong for Save.
 *   - Partner brand color is passed in, never hardcoded, so a primary
 *     button follows the active partner the way the rest of the app does.
 *   - Real disabled and loading states are built in, because hand-rolled
 *     buttons routinely forget them and that is a real UX bug (double
 *     submits, no feedback on slow saves).
 */

import React from 'react';

const SIZES = {
  sm: { padding: '4px 10px', fontSize: 11, radius: 6, gap: 5 },
  md: { padding: '6px 14px', fontSize: 13, radius: 7, gap: 6 },
  lg: { padding: '8px 18px', fontSize: 14, radius: 8, gap: 7 },
};

// The fill and edge of a primary button. A hex gets the soft gradient and a darker edge. A CSS variable (var(--gov-accent-slate)) cannot be shaded, so it is a flat fill with the
// same color edge; before this the shade math produced rgb(NaN...) and the browser dropped the whole background.
export function primaryFill(color) {
  if (!/^#[0-9a-f]{6}$/i.test(String(color || ''))) return { background: color, border: `1px solid ${color}` };
  const shade = (amt) => {
    const n = parseInt(color.slice(1), 16);
    const ch = (v) => Math.max(0, Math.round(v * (1 - amt)));
    return `rgb(${ch((n >> 16) & 255)}, ${ch((n >> 8) & 255)}, ${ch(n & 255)})`;
  };
  return { background: `linear-gradient(180deg, ${color} 0%, ${shade(0.1)} 100%)`, border: `1px solid ${shade(0.18)}` };
}

/**
 * Real variant palette. `color` is the active partner brand color and
 * only affects `primary` -- the others are intentionally neutral so a
 * screen full of buttons doesn't turn into a wall of brand color.
 */
function variantStyle(variant, color, disabled) {
  // Real, 2026-08-25 (Stinger direct, round 3): "they don't have to
  // be artsy they just don't need to be flintstone blocks" -- real
  // correction, the problem was bulk and weight, not lack of visible
  // depth. Pulled padding/radius down below where they started,
  // dropped the heavy diffuse shadow for something crisp and tight,
  // and font-weight comes down too (600, not 700) -- clean and light
  // reads as refined; a big bold gradient slab reads as a block no
  // matter how deliberate the gradient is.
  const shade = (hex, amt) => {
    const n = parseInt(hex.slice(1), 16);
    const r = Math.max(0, Math.round(((n >> 16) & 255) * (1 - amt)));
    const g = Math.max(0, Math.round(((n >> 8) & 255) * (1 - amt)));
    const b = Math.max(0, Math.round((n & 255) * (1 - amt)));
    return `rgb(${r}, ${g}, ${b})`;
  };
  const depthShadow = '0 1px 2px rgba(15, 23, 42, 0.12)';
  const filledGradient = (hex) => `linear-gradient(180deg, ${hex} 0%, ${shade(hex, 0.1)} 100%)`;

  const base = {
    primary: {
      // A hex gets the soft gradient and a darker edge. A CSS variable (var(--gov-accent-slate)) cannot be shaded, so it is a flat fill with the same color edge; without this the
      // gradient math produced rgb(NaN...) and the browser dropped the whole background.
      ...primaryFill(color),
      color: '#ffffff',
      boxShadow: depthShadow,
    },
    secondary: {
      background: '#ffffff',
      color: '#1a2133',
      border: '1px solid #d3d8e0',
    },
    danger: {
      background: '#ffffff',
      color: '#b91c1c',
      border: '1px solid #fecaca',
    },
    // Real, deliberate: danger-solid exists for the genuinely
    // destructive confirm inside a modal, where an outline button is
    // too quiet for what is about to happen.
    dangerSolid: {
      background: filledGradient('#b91c1c'),
      color: '#ffffff',
      border: `1px solid ${shade('#b91c1c', 0.18)}`,
      boxShadow: depthShadow,
    },
    success: {
      background: filledGradient('#15803d'),
      color: '#ffffff',
      border: `1px solid ${shade('#15803d', 0.18)}`,
      boxShadow: depthShadow,
    },
    ghost: {
      background: 'transparent',
      color: '#64748b',
      border: '1px solid transparent',
    },
  }[variant] || {};

  if (disabled) {
    return { ...base, opacity: 0.5, cursor: 'not-allowed', boxShadow: 'none' };
  }
  return base;
}

/**
 * Button
 *
 * @param {string}   variant   primary | secondary | danger | dangerSolid | success | ghost
 * @param {string}   size      sm | md | lg
 * @param {string}   color     active partner brand color (drives `primary`)
 * @param {boolean}  disabled
 * @param {boolean}  loading   shows loadingText and blocks clicks
 * @param {string}   loadingText
 * @param {boolean}  fullWidth
 * @param {node}     icon      optional leading glyph/element
 * @param {boolean}  iconOnly  real, added 2026-08-18 (#1510 sweep) --
 *                   the recurring '×' icon-close-button pattern (found
 *                   in Modal.jsx, SequenceHealth.jsx, and others) had
 *                   no real fit in this component before now. Square
 *                   padding, no forced min-width, transparent by
 *                   default (still respects `variant` if set).
 */
export default function Button({
  children,
  variant = 'secondary',
  size = 'md',
  color = '#99182c',
  disabled = false,
  loading = false,
  loadingText,
  fullWidth = false,
  icon = null,
  iconOnly = false,
  style = {},
  onClick,
  type = 'button',
  ...rest
}) {
  const sz = SIZES[size] || SIZES.md;
  const isOff = disabled || loading;
  const iconOnlyStyle = iconOnly
    ? { padding: size === 'sm' ? '3px' : size === 'lg' ? '7px' : '5px', background: 'none', border: 'none', color: '#94a3b8', fontWeight: 400 }
    : {};

  return (
    <button
      type={type}
      // Real guard: a loading button must not fire again. Hand-rolled
      // buttons routinely miss this and it causes real double-submits.
      onClick={isOff ? undefined : onClick}
      disabled={isOff}
      style={{
        display: fullWidth ? 'flex' : 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sz.gap,
        width: fullWidth ? '100%' : undefined,
        padding: sz.padding,
        fontSize: sz.fontSize,
        fontWeight: 600,
        borderRadius: sz.radius,
        cursor: isOff ? 'not-allowed' : 'pointer',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        transition: 'filter 0.12s, opacity 0.12s, transform 0.08s, box-shadow 0.12s',
        ...variantStyle(variant, color, isOff),
        ...iconOnlyStyle,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (isOff) return;
        e.currentTarget.style.filter = 'brightness(1.04)';
        if (variant === 'primary' || variant === 'dangerSolid' || variant === 'success') {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(15, 23, 42, 0.16)';
        }
      }}
      onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}
      onMouseDown={(e) => { if (!isOff) e.currentTarget.style.transform = 'translateY(0px) scale(0.98)'; }}
      onMouseUp={(e) => { if (!isOff) e.currentTarget.style.transform = 'translateY(-1px)'; }}
      {...rest}
    >
      {icon && !loading && icon}
      {loading ? (loadingText || 'Working…') : children}
    </button>
  );
}
